/**
 * Simply Steaks theme - site-wide behaviour.
 * Vanilla JS, no dependencies: header + announcement bar, Ajax cart + drawer,
 * product variants and gallery, tilt / fall-in effects, editor re-init.
 */
(function () {
  'use strict';

  var theme = window.ssTheme || {};
  var routes = theme.routes || {};
  var strings = theme.strings || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(selector, context) {
    return (context || document).querySelector(selector);
  }
  function $$(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  /* ------------------------------------------------------------------ *
   * Header: sticky height variable, mobile menu, announcement rotation *
   * ------------------------------------------------------------------ */
  var announceTimers = new WeakMap();

  function setHeaderHeight() {
    var header = $('[data-header]');
    if (!header) return;
    document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
  }

  function initAnnouncement(root) {
    var bar = $('[data-announce]', root) || (root && root.matches && root.matches('[data-announce]') ? root : null);
    if (!bar || announceTimers.has(bar)) return;
    var messages = $$('.ss-announce__msg', bar);
    if (messages.length < 2 || reduceMotion) return;
    var index = 0;
    var interval = parseInt(bar.getAttribute('data-interval'), 10) || 3200;
    var timer = window.setInterval(function () {
      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    }, interval);
    announceTimers.set(bar, timer);
  }

  function setMenu(open) {
    var toggle = $('[data-menu-toggle]');
    var nav = $('[data-mobile-nav]');
    if (!toggle || !nav) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    nav.hidden = !open;
    var openIcon = $('[data-menu-open]', toggle);
    var closeIcon = $('[data-menu-close]', toggle);
    if (openIcon) openIcon.hidden = open;
    if (closeIcon) closeIcon.hidden = !open;
    window.requestAnimationFrame(setHeaderHeight);
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-menu-toggle]');
    if (toggle) {
      setMenu(toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }
    if (event.target.closest('.ss-mobile-nav__link')) setMenu(false);
  });

  /* ------------------------------------------------------------------ *
   * Cart                                                               *
   * ------------------------------------------------------------------ */
  var drawer = null;
  var lastFocus = null;

  function getDrawer() {
    drawer = $('[data-cart-drawer]');
    return drawer;
  }

  function openDrawer() {
    if (!getDrawer()) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ss-no-scroll');
    var panel = $('.ss-drawer__panel', drawer);
    if (panel) panel.focus({ preventScroll: true });
  }

  function closeDrawer() {
    if (!getDrawer() || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ss-no-scroll');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-drawer-close]')) {
      closeDrawer();
      return;
    }
    var cartLink = event.target.closest('[data-cart-link]');
    if (cartLink && theme.cartType === 'drawer' && getDrawer() && window.location.pathname !== routes.cart) {
      event.preventDefault();
      openDrawer();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    closeDrawer();
    setMenu(false);
  });

  function cartSectionIds() {
    return $$('[data-cart-section]').map(function (el) {
      return el.getAttribute('data-section-id');
    });
  }

  function cartRequest(url, body) {
    var payload = Object.assign({}, body, {
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname
    });
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || strings.error);
        }
        return data;
      });
    });
  }

  function renderSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var target = $('[data-cart-section][data-section-id="' + id + '"]');
      if (!target || !sections[id]) return;
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      var fresh = doc.querySelector('[data-cart-section][data-section-id="' + id + '"]');
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = !count;
    });
  }

  function refreshCartFromServer() {
    return fetch(routes.cartJson, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        updateCount(cart.item_count);
        return cart;
      });
  }

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) {
      button.setAttribute('data-loading', 'true');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('data-loading');
      button.removeAttribute('aria-busy');
    }
  }

  function flashAdded(button) {
    var label = $('[data-atc-label]', button);
    var icon = $('[data-atc-icon]', button);
    if (!label) return;
    if (!button.hasAttribute('data-label-original')) {
      button.setAttribute('data-label-original', label.textContent);
    }
    var original = button.getAttribute('data-label-original');
    var originalIcon = icon ? icon.innerHTML : '';
    label.textContent = strings.added || 'Added';
    if (icon) {
      icon.innerHTML =
        '<svg class="ss-icon ss-icon--xs" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    }
    button.classList.add('is-added');
    window.setTimeout(function () {
      label.textContent = original;
      if (icon) icon.innerHTML = originalIcon;
      button.classList.remove('is-added');
    }, 1400);
  }

  function showFormError(form, message) {
    var box = $('[data-form-error]', form);
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest ? event.target.closest('form[data-type="add-to-cart-form"]') : null;
    if (!form) return;
    event.preventDefault();

    var button = $('[data-atc-button]', form);
    if (!button || button.disabled || button.hasAttribute('data-loading')) return;

    var idInput = $('[name="id"]', form);
    var qtyInput = $('[name="quantity"]', form);
    var id = idInput ? Number(idInput.value) : 0;
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    if (!id) return;

    showFormError(form, '');
    setBusy(button, true);

    cartRequest(routes.cartAdd, { items: [{ id: id, quantity: quantity }] })
      .then(function (data) {
        renderSections(data.sections);
        return refreshCartFromServer();
      })
      .then(function () {
        flashAdded(button);
        if (theme.cartType === 'drawer' && getDrawer()) {
          openDrawer();
        } else {
          window.location.href = routes.cart;
        }
      })
      .catch(function (error) {
        showFormError(form, error.message || strings.error);
      })
      .then(function () {
        setBusy(button, false);
      });
  });

  function changeLine(line, quantity) {
    var scopes = $$('[data-cart-section]');
    scopes.forEach(function (el) {
      el.setAttribute('aria-busy', 'true');
    });
    return cartRequest(routes.cartChange, { line: Number(line), quantity: quantity })
      .then(function (cart) {
        updateCount(cart.item_count);
        renderSections(cart.sections);
      })
      .catch(function () {
        // Fall back to the server-rendered page so quantities are never stale.
        return refreshCartFromServer().then(function () {
          if (window.location.pathname === routes.cart) window.location.reload();
        });
      })
      .then(function () {
        $$('[data-cart-section]').forEach(function (el) {
          el.removeAttribute('aria-busy');
        });
      });
  }

  document.addEventListener('click', function (event) {
    var step = event.target.closest('[data-qty-step]');
    if (step) {
      var input = $('[data-qty-input]', step.closest('.ss-qty'));
      if (!input) return;
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.getAttribute('data-qty-step')));
      input.value = next;
      changeLine(step.getAttribute('data-line'), next);
      return;
    }
    var remove = event.target.closest('[data-remove-line]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.getAttribute('data-remove-line'), 0);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest ? event.target.closest('[data-qty-input]') : null;
    if (!input) return;
    var value = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(input.getAttribute('data-line'), value);
  });

  /* ------------------------------------------------------------------ *
   * Product page: variants, gallery, quantity                          *
   * ------------------------------------------------------------------ */
  function initProduct(section) {
    if (!section || section.hasAttribute('data-product-ready')) return;
    var jsonEl = $('[data-product-json]', section);
    if (!jsonEl) return;
    section.setAttribute('data-product-ready', '');

    var product;
    try {
      product = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }

    var sectionId = section.getAttribute('data-section-id');
    var productUrl = section.getAttribute('data-product-url');
    var optionInputs = $$('[data-option-position]', section);
    var mainImage = $('[data-gallery-main]', section);

    function selectedOptions() {
      var chosen = [];
      optionInputs.forEach(function (input) {
        if (input.checked) chosen[Number(input.getAttribute('data-option-position')) - 1] = input.value;
      });
      return chosen;
    }

    function findVariant() {
      var chosen = selectedOptions();
      return product.variants.filter(function (variant) {
        return chosen.every(function (value, index) {
          var optionValue = variant.options ? variant.options[index] : variant['option' + (index + 1)];
          return optionValue === value;
        });
      })[0];
    }

    function swapImage(src, alt) {
      if (!mainImage || !src) return;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = src;
      if (alt !== undefined) mainImage.alt = alt || '';
    }

    function applyVariant(variant) {
      var button = $('[data-atc-button]', section);
      var idInput = $('[data-variant-id]', section);
      if (!variant) {
        if (button) {
          button.disabled = true;
          var unavailableLabel = $('[data-atc-label]', button);
          if (unavailableLabel) unavailableLabel.textContent = 'Unavailable';
        }
        return;
      }
      if (idInput) idInput.value = variant.id;

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      var featured = variant.featured_image && variant.featured_image.src;
      if (featured) swapImage(featured, variant.featured_image.alt);

      fetch(productUrl + '?variant=' + variant.id + '&section_id=' + sectionId)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshPrice = $('[data-price-wrap]', doc);
          var currentPrice = $('[data-price-wrap]', section);
          if (freshPrice && currentPrice) currentPrice.innerHTML = freshPrice.innerHTML;

          var freshButton = $('[data-atc-button]', doc);
          if (freshButton && button) {
            button.disabled = freshButton.disabled;
            var label = $('[data-atc-label]', button);
            var freshLabel = $('[data-atc-label]', freshButton);
            if (label && freshLabel) label.textContent = freshLabel.textContent;
            button.removeAttribute('data-label-original');
          }
        })
        .catch(function () {
          /* keep the current price if the refresh fails */
        });
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        applyVariant(findVariant());
      });
    });

    $$('[data-gallery-thumb]', section).forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        swapImage(thumb.getAttribute('data-src'), thumb.getAttribute('data-alt'));
        $$('[data-gallery-thumb]', section).forEach(function (other) {
          other.classList.toggle('is-active', other === thumb);
        });
      });
    });

    $$('[data-product-qty-step]', section).forEach(function (button) {
      button.addEventListener('click', function () {
        var input = $('[data-product-qty]', section);
        if (!input) return;
        var next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.getAttribute('data-product-qty-step')));
        input.value = next;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Related products (Shopify recommendations API)                     *
   * ------------------------------------------------------------------ */
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach(function (el) {
      if (el.hasAttribute('data-recs-ready')) return;
      el.setAttribute('data-recs-ready', '');
      var url = el.getAttribute('data-url');
      if (!url) return;
      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = $('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim().length) {
            el.innerHTML = fresh.innerHTML;
          } else {
            el.hidden = true;
          }
        })
        .catch(function () {
          el.hidden = true;
        });
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection sort                                                    *
   * ------------------------------------------------------------------ */
  document.addEventListener('change', function (event) {
    var select = event.target.closest ? event.target.closest('[data-sort-select]') : null;
    if (!select) return;
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ------------------------------------------------------------------ *
   * Effects: pointer tilt (mouse only) and fall-in on scroll           *
   * ------------------------------------------------------------------ */
  if (!reduceMotion) {
    document.addEventListener('pointermove', function (event) {
      if (event.pointerType !== 'mouse') return;
      var el = event.target.closest ? event.target.closest('[data-tilt]') : null;
      if (!el) return;
      var rect = el.getBoundingClientRect();
      var px = (event.clientX - rect.left) / rect.width;
      var py = (event.clientY - rect.top) / rect.height;
      var max = 6;
      el.style.transform =
        'perspective(900px) rotateX(' + ((0.5 - py) * max * 2).toFixed(2) + 'deg) rotateY(' +
        ((px - 0.5) * max * 2).toFixed(2) + 'deg) scale3d(1.02,1.02,1.02)';
    });
    document.addEventListener('pointerout', function (event) {
      var el = event.target.closest ? event.target.closest('[data-tilt]') : null;
      if (el && !el.contains(event.relatedTarget)) el.style.transform = '';
    });
  }

  var fallObserver = null;
  function initFallIn(root) {
    var items = $$('[data-fall-in]', root);
    if (!items.length) return;
    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    if (!fallObserver) {
      fallObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var el = entry.target;
            var siblings = $$('[data-fall-in]', el.parentElement);
            var index = Math.max(0, siblings.indexOf(el));
            el.style.transitionDelay = Math.min(index, 6) * 70 + 'ms';
            el.classList.add('is-in');
            fallObserver.unobserve(el);
          });
        },
        { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
      );
    }
    items.forEach(function (el) {
      if (!el.classList.contains('is-in')) fallObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot + theme-editor re-init                                        *
   * ------------------------------------------------------------------ */
  function init(root) {
    initAnnouncement(root || document);
    $$('[data-product-section]', root).forEach(initProduct);
    initRecommendations(root);
    initFallIn(root);
    setHeaderHeight();
  }

  function boot() {
    init(document);
    window.addEventListener('resize', setHeaderHeight);
    window.addEventListener('load', setHeaderHeight);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var bar = $('[data-announce]', event.target);
    if (bar && announceTimers.has(bar)) {
      window.clearInterval(announceTimers.get(bar));
      announceTimers.delete(bar);
    }
  });

  window.ssTheme = Object.assign(theme, {
    openCart: openDrawer,
    closeCart: closeDrawer,
    refreshCart: refreshCartFromServer
  });
})();
